import { generateClient, GraphQLResult } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { Project, ProjectForResolver, UserForResolver } from "db/definitions";
import { createProject as createP, createProjectForPut } from "db/queries/projectQueries";
import { recordSentry } from "utils/recordSentry";
import { v4 as uuid } from "uuid";
import { createProject } from "../graphql/mutations";

/**
 * Creates a project document in the db
 * @param newProjectData The project data
 * @param online Whether or not the user is online
 * @param userDoc The user document
 * @returns The created project document
 */
async function createProjectAmplify(
	newProjectData: Pick<ProjectForResolver, "name" | "description">,
	online: boolean,
	userDoc: UserForResolver,
): Promise<Project | null> {
	// Need to assign group, id, public, users, createdAt, updatedAt
	try {
		if (online) {
			const userSession = await fetchAuthSession();
			if (
				userSession.tokens &&
				userSession.tokens.accessToken.payload["cognito:groups"] &&
				Array.isArray(userSession.tokens.accessToken.payload["cognito:groups"])
			) {
				const groups = userSession.tokens.accessToken.payload["cognito:groups"] as string[];
				const group = groups.find((grp) => !grp.includes("_"));
				if (group) {
					const id = uuid();
					const timestamp = new Date().toISOString();
					const projectData: ProjectForResolver = {
						users: [],
						name: newProjectData.name,
						description: newProjectData.description,
						public: true,
						group,
						id: id,
						createdAt: timestamp,
						updatedAt: timestamp,
					};
					interface QueryResponse {
						createProject: Project;
					}
					const client = generateClient({ authMode: "userPool" });
					const created = (await client.graphql({
						query: createProject,
						variables: {
							input: projectData,
						},
						authMode: "userPool",
					})) as unknown as GraphQLResult<QueryResponse>;
					return created.data.createProject;
				}
			}
		} else {
			const timestamp = new Date().toISOString();
			const project: Project = {
				id: uuid(),
				name: newProjectData.name,
				description: newProjectData.description,
				group: userDoc.group,
				public: true,
				users: [],
				// if they create the project then immediately try to create formGroup, it errors if there's no formGroup property
				formGroups: { items: [] },
				createdAt: timestamp,
				updatedAt: timestamp,
			};

			await createP(createProjectForPut(project));
			return project;
		}
	} catch (err) {
		recordSentry({ error: err, msg: "Error creating new project:", showModal: true });
	}
	return null;
}

export default createProjectAmplify;
